import cn from 'classnames';
import { FC, useRef } from 'react';
import { useIntl } from 'react-intl';

import { DentalPhotoAlbum } from '@/shared/api/protocol-ts/model/dto_dental_photo_album_pb';
import {
  useAppDispatch,
  useAppSelector,
  useElementWidth,
} from '@/shared/hooks';
import { trackEvent } from '@/shared/lib';
import { Icon } from '@/shared/ui';

import { getAlbumDisplayName } from '@/entities/album';
import { assetsModel } from '@/entities/assets';
import { ModalID, modalModel, RemoveModal } from '@/entities/modal';

import { TransformedPhoto } from '@/features/transformedPhoto';

import {
  GALLERY_GAP,
  GALLERY_ROW_HEIGHT,
  GALLERY_UPLOAD_TILE_ID,
} from '../../config';
import {
  useDentalPhotoAlbumActions,
  useDentalPhotoAlbumPermissions,
} from '../../hooks';
import { getPhotoAspectRatio, layoutJustifiedRows } from '../../lib';
import { AlbumMediaCountsLabel } from '../AlbumMediaCountsLabel/AlbumMediaCountsLabel';
import { AlbumPopover } from '../AlbumPopover/AlbumPopover';

import styles from './DentalPhotoAlbumGallery.module.scss';

type DentalPhotoAlbumGalleryProps = {
  album: DentalPhotoAlbum;
  patientID: string;
  className?: string;
  testID?: string;
};

// Albums and assets already arrive with the patient profile stream, so the
// only network cost here is the lazily loaded thumbnails.
export const DentalPhotoAlbumGallery: FC<DentalPhotoAlbumGalleryProps> = (
  props,
) => {
  const { album, patientID, className, testID } = props;

  const { formatMessage, formatDate } = useIntl();
  const dispatch = useAppDispatch();
  const rowsRef = useRef<HTMLDivElement>(null);
  const containerWidth = useElementWidth(rowsRef);

  const assets = useAppSelector(assetsModel.selectors.selectEntities);
  const { canEditAlbums, canUpload } =
    useDentalPhotoAlbumPermissions(patientID);
  const {
    isDownloading,
    isDeleting,
    isRemoveModalOpen,
    openRemoveModal,
    closeRemoveModal,
    handleEditAlbum,
    handleDownloadAlbum,
    handleConfirmDeleteAlbum,
    handleUploadToAlbum,
  } = useDentalPhotoAlbumActions({ album, patientID });

  const albumTime = album.Time?.toDate();
  const displayName = getAlbumDisplayName(album.Name);
  const photoIDs = album.StudyAssetIDs;

  const rows = layoutJustifiedRows({
    tiles: [
      ...photoIDs.map((photoID) => ({
        id: photoID,
        aspectRatio: getPhotoAspectRatio(assets[photoID]),
      })),
      ...(canUpload ? [{ id: GALLERY_UPLOAD_TILE_ID, aspectRatio: 1 }] : []),
    ],
    containerWidth,
    targetHeight: GALLERY_ROW_HEIGHT,
    gap: GALLERY_GAP,
  });

  const handlePhotoClick = (photoID: string) => {
    trackEvent({
      eventName: 'DentalPhotosPhotoClicked',
      properties: { photo_id: photoID, album_id: album.ID },
    });

    dispatch(
      modalModel.actions.openModal({
        modalID: ModalID.DentalPhotoSlider,
        data: { albumID: album.ID, activePhotoID: photoID, canEditAlbums },
      }),
    );
  };

  return (
    <div
      className={cn(styles.container, className)}
      data-component="dental-photo-album-gallery"
      data-testid={testID}
    >
      <div className={styles.header}>
        <div className={styles.title}>
          {albumTime && (
            <span className={cn('p2', styles.time)}>
              {formatDate(albumTime, { timeStyle: 'short' })}
            </span>
          )}
          <span className={cn('h5', styles.name)}>
            {displayName ??
              formatMessage({
                id: 'studyUpload.DentalPhotos',
                defaultMessage: 'Dental Photos',
              })}
          </span>
          <span className={cn('p3', styles.meta)}>
            <AlbumMediaCountsLabel assetIDs={photoIDs} />
          </span>
        </div>

        <AlbumPopover
          canEditAlbums={canEditAlbums}
          isDownloading={isDownloading}
          isDeleting={isDeleting}
          onEditAlbum={handleEditAlbum}
          onDownloadAlbum={handleDownloadAlbum}
          onDeleteAlbum={openRemoveModal}
        />
      </div>

      <div ref={rowsRef} className={styles.rows}>
        {rows.map((row, rowIndex) => (
          <div key={rowIndex} className={styles.row}>
            {row.tiles.map((tile) =>
              tile.id === GALLERY_UPLOAD_TILE_ID ? (
                <button
                  key={tile.id}
                  type="button"
                  className={cn(styles.tile, styles.uploadTile)}
                  style={{ width: tile.width, height: row.height }}
                  aria-label={formatMessage({
                    id: 'dentalPhotos.upload.title',
                    defaultMessage: 'Upload photos or videos',
                  })}
                  onClick={handleUploadToAlbum}
                >
                  <Icon name="plus" size={24} />
                </button>
              ) : (
                <button
                  key={tile.id}
                  type="button"
                  className={styles.tile}
                  data-component="dental-photo-gallery-tile"
                  style={{ width: tile.width, height: row.height }}
                  aria-label={formatMessage(
                    {
                      id: 'patientProfile.photos.openPhoto',
                      defaultMessage: 'Open photo {index} of {total}',
                    },
                    {
                      index: photoIDs.indexOf(tile.id) + 1,
                      total: photoIDs.length,
                    },
                  )}
                  onClick={() => handlePhotoClick(tile.id)}
                >
                  <TransformedPhoto
                    photoID={tile.id}
                    size="preview"
                    className={styles.media}
                    alt=""
                  />
                </button>
              ),
            )}
          </div>
        ))}
      </div>

      <RemoveModal
        isOpen={isRemoveModalOpen}
        message={formatMessage({
          id: 'dentalPhoto.removePhotoModal.message',
          defaultMessage: 'This action is irreversible',
        })}
        title={formatMessage({
          id: 'dentalPhoto.removeAlbumModal.title',
          defaultMessage: 'Remove this album',
        })}
        onCancel={closeRemoveModal}
        onRemoveCallback={handleConfirmDeleteAlbum}
      />
    </div>
  );
};
